/**
 * 承認キューのカード1枚分の操作（承認・予約・投稿・却下・編集・個別承認・結果不明の解決）。
 * エラーはカード単位で持ち、成功時のお知らせはページ側（notify）へ渡す —
 * 承認すると投稿は別セクションへ移ってカードが消えるため、カード内に出すと読めない
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, errorMessage, lintIssuesOf, unwrap, type LintIssue } from '../../lib/api'
import { queryKeys } from '../../lib/queries'
import { formatSchedule } from '../../lib/datetime'
import type { ApproveResult, QueueItem } from '../../lib/postTypes'

export interface ActionError {
  message: string
  issues: LintIssue[]
}

export type ResolveBody = { action: 'mark_published'; externalUrl?: string } | { action: 'retry' }

export type Notify = (message: string) => void

export function useQueueActions(item: Pick<QueueItem, 'post' | 'activeVersion'>, notify: Notify) {
  const { post } = item
  // 承認は「画面で見ている版」に対してだけ成立させる（見ている間に本文が差し替わったらサーバーが 409 — Codex 2巡目 R2）
  const versionId = item.activeVersion?.id ?? null
  const qc = useQueryClient()
  const [error, setError] = useState<ActionError | null>(null)
  const title = `「${post.title || '(無題)'}」`

  const done = (message?: string) => {
    void qc.invalidateQueries({ queryKey: queryKeys.queue })
    void qc.invalidateQueries({ queryKey: queryKeys.scheduledPosts })
    setError(null)
    if (message) notify(message)
  }
  // サーバーのエラー（Guard BLOCK の 400・残高不足の 402・状態競合の 409 等）をカード単位で表示する。
  // onError が無いとエラーが捨てられ、ボタンを押しても無反応に見える
  const fail = (e: unknown) => setError({ message: errorMessage(e), issues: lintIssuesOf(e) })

  const approve = useMutation({
    mutationFn: async (p: { scheduledAt?: string; publishNow?: boolean }) =>
      unwrap(await apiClient.post<ApproveResult>(`/posts/${post.id}/approve`, { ...p, versionId })),
    onSuccess: (r, p) => {
      if (r?.status === 'publishing') done(`${title}の投稿を開始しました。`)
      else if (r?.status === 'scheduled') done(`${title}を ${formatSchedule(p.scheduledAt)} に予約しました。`)
      else done(
        `${title}をストックに入れました。ストックの投稿は「ストック・予約済み」から「今すぐ投稿」するか日時を予約するまで投稿されません（定期投稿ルールの設定は準備中です）。`,
      )
    },
    onError: fail,
  })
  const reject = useMutation({
    mutationFn: async () => unwrap(await apiClient.post(`/posts/${post.id}/reject`)),
    onSuccess: () => done(`${title}を却下しました。`),
    onError: fail,
  })
  /** 承認済み・予約済み・失敗からの投稿（pending_approval からは approve の publishNow を使う） */
  const publish = useMutation({
    mutationFn: async () => unwrap(await apiClient.post(`/posts/${post.id}/publish`)),
    onSuccess: () => done(`${title}の投稿を開始しました。`),
    onError: fail,
  })
  const approveGated = useMutation({
    mutationFn: async (pchId: string) =>
      unwrap(await apiClient.post(`/posts/${post.id}/channels/${pchId}/approve-gated`, { versionId })),
    onSuccess: () => done(),
    onError: fail,
  })
  // 本文編集（BLOCK からの唯一の復帰経路 — DESIGN §2.3）
  const edit = useMutation({
    mutationFn: async (xText: string) => unwrap(await apiClient.post(`/posts/${post.id}/edit`, { xText })),
    onSuccess: () => done(`${title}の本文を保存しました。安全チェックをやり直しています。`),
    onError: fail,
  })
  const resolve = useMutation({
    mutationFn: async (p: { pchId: string; body: ResolveBody }) =>
      unwrap(await apiClient.post(`/posts/${post.id}/channels/${p.pchId}/resolve`, p.body)),
    onSuccess: (_r, p) => done(p.body.action === 'mark_published'
      ? `${title}を投稿済みとして記録しました。`
      : `${title}を再投稿できる状態に戻しました。`),
    onError: fail,
  })

  // 「投稿は成立したが確かめてほしいこと」（Instagram がキャプションを付けずに公開した等）を確認済みにする。
  // 配信行ごとの操作なので、全部投稿済みの投稿でも、一部だけ投稿済みの投稿でも使える
  const ackNote = useMutation({
    mutationFn: async (pchId: string) => unwrap(await apiClient.post(`/posts/${post.id}/channels/${pchId}/ack-note`)),
    onSuccess: () => {
      done(`${title}の注意を確認済みにしました。`)
      // 押したボタンはカードごと消えるので、キーボード操作の位置を見出しに戻す
      document.getElementById('queue-heading')?.focus()
    },
    onError: fail,
  })

  const pending = approve.isPending || reject.isPending || publish.isPending
    || approveGated.isPending || edit.isPending || resolve.isPending || ackNote.isPending

  return { approve, reject, publish, approveGated, edit, resolve, ackNote, pending, error }
}

export type QueueActions = ReturnType<typeof useQueueActions>
